import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Combobox 컴포넌트
 * 자유 텍스트 입력 + 기존 값 제안 목록을 함께 지원합니다.
 *
 * 사용 예시:
 * <Combobox
 *   value={location}
 *   onChange={setLocation}
 *   suggestions={['1공장', '2공장', '창고 A']}
 *   placeholder="현장 위치 입력"
 *   label="위치"
 * />
 */
export function Combobox({
  value,
  onChange,
  suggestions,
  placeholder,
  label,
  className = '',
  disabled = false,
}: ComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Filter suggestions based on current input value.
  // Show all suggestions when the field is empty or the value matches one exactly.
  const filtered =
    value.trim() === ''
      ? suggestions
      : suggestions.filter((s) =>
          s.toLowerCase().includes(value.toLowerCase())
        );

  // Reset highlight when the filtered list changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filtered.length]);

  // Scroll the highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[highlightedIndex] as HTMLElement;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  // Close dropdown when clicking outside (input + portal dropdown 모두 고려)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insideDropdown = listRef.current?.contains(target);
      if (!insideContainer && !insideDropdown) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 드롭다운 위치 계산 (portal + fixed) — 부모 overflow 영향 회피
  useEffect(() => {
    if (!isOpen) {
      setDropdownStyle(null);
      return;
    }
    const updatePosition = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      setDropdownStyle({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value);
    setIsOpen(true);
  }

  function handleInputFocus() {
    if (!disabled) setIsOpen(true);
  }

  function handleSelect(suggestion: string) {
    onChange(suggestion);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : 0
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filtered.length - 1
        );
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filtered[highlightedIndex] !== undefined) {
          handleSelect(filtered[highlightedIndex]);
        } else {
          // Confirm free-text input as-is
          setIsOpen(false);
          setHighlightedIndex(-1);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;

      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  }

  const showDropdown = isOpen && filtered.length > 0;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-ink-700 mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls="combobox-listbox"
          aria-activedescendant={
            highlightedIndex >= 0
              ? `combobox-option-${highlightedIndex}`
              : undefined
          }
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={[
            'w-full rounded-control border px-3 py-2 pr-8 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
            'placeholder:text-ink-400',
            disabled
              ? 'bg-ink-100 text-ink-400 cursor-not-allowed border-ink-200'
              : 'bg-white text-ink-900 border-ink-300 hover:border-ink-400',
          ].join(' ')}
        />

        {/* Chevron icon */}
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            if (isOpen) {
              setIsOpen(false);
              setHighlightedIndex(-1);
            } else {
              setIsOpen(true);
              inputRef.current?.focus();
            }
          }}
          className={[
            'absolute inset-y-0 right-0 flex items-center px-2',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          ].join(' ')}
        >
          <svg
            className={`h-4 w-4 text-ink-400 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/* Dropdown list — portal + fixed로 부모 overflow 회피 */}
      {showDropdown && dropdownStyle && createPortal(
        <ul
          ref={listRef}
          id="combobox-listbox"
          role="listbox"
          style={{
            position: 'fixed',
            top: dropdownStyle.top,
            left: dropdownStyle.left,
            width: dropdownStyle.width,
            zIndex: 1000,
          }}
          className={[
            'rounded-card border border-ink-200 bg-white py-1 shadow-lg',
            'max-h-56 overflow-y-auto',
          ].join(' ')}
        >
          {filtered.map((suggestion, index) => {
            const isHighlighted = index === highlightedIndex;
            return (
              <li
                key={suggestion}
                id={`combobox-option-${index}`}
                role="option"
                aria-selected={isHighlighted}
                onMouseDown={(e) => {
                  // Prevent blur on input before click registers
                  e.preventDefault();
                  handleSelect(suggestion);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={[
                  'cursor-pointer select-none px-3 py-2 text-sm',
                  isHighlighted
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-ink-900 hover:bg-ink-50',
                ].join(' ')}
              >
                {suggestion}
              </li>
            );
          })}
        </ul>,
        document.body,
      )}
    </div>
  );
}
